<div class="config-container">
  <header class="config-header">
    <h1>JSON→固定長コンバータ</h1>
  </header>

  @if (error()) {
    <div class="error-banner">{{ error() }}</div>
  }

  <form [formGroup]="configForm" class="glass-form">
    <section class="form-section">
      <div class="section-title">
        <span class="icon">🔑</span>
        <h2>読み込み</h2>
      </div>
      <div class="form-group">
        <label for="wbsTaskId">
          WBS Task ID (読み込み対象)
          <span class="field-hint">保存済み設定を読み込むためのID</span>
        </label>
        <div style="display: flex; gap: 8px;">
          <input id="wbsTaskId" type="text" formControlName="wbsTaskId" style="flex: 1;" />
          <button
            type="button"
            class="submit-btn"
            (click)="fetchSavedConfig()"
            [disabled]="isLoading()"
            style="width: auto; padding: 0 16px; margin-top: 0;"
          >
            {{ isLoading() ? '...' : '読込' }}
          </button>
        </div>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">📝</span>
        <h2>基本情報</h2>
      </div>
      <div class="grid-fields">
        <div class="form-group">
          <label for="id">タスクID<span class="field-hint">実行時に使用する固有の識別子</span></label>
          <input id="id" type="text" formControlName="id" />
        </div>
        <div class="form-group">
          <label for="name">タスク名称<span class="field-hint">管理画面などで表示される名前</span></label>
          <input id="name" type="text" formControlName="name" />
        </div>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">🎯</span>
        <h2>コンバーター対象</h2>
      </div>
      <div class="grid-fields">
        <div class="form-group">
          <label for="searchPath">
            入力JSONファイルパス
            <span class="field-hint">オブジェクトの配列が入ったJSONファイル（fileSystemId）</span>
          </label>
          <div class="input-with-upload">
            <input id="searchPath" type="text" formControlName="searchPath" />
            <app-file-transfer
              [initialFileId]="configForm.get('searchPath')?.value || ''"
              [compact]="true"
            ></app-file-transfer>
          </div>
        </div>
        <div class="form-group">
          <label for="baseObjectPath">
            キー反映用の基準パス（任意）
            <span class="field-hint">配列がネストしているとき（例: data.items）。空ならルートの配列またはオブジェクトを使用</span>
          </label>
          <input id="baseObjectPath" type="text" formControlName="baseObjectPath" placeholder="例: data.list" />
        </div>
        <div class="form-group">
          <label for="savePath">
            出力固定長ファイルパス
            <span class="field-hint">Shift_JIS の固定長テキストの保存先</span>
          </label>
          <input id="savePath" type="text" formControlName="savePath" />
        </div>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">📊</span>
        <h2>エンティティスキーマ</h2>
        <span class="section-hint">固定長レコードのフィールド定義（出力レイアウト）</span>
      </div>
      <p class="length-total-summary">
        <span class="length-total-row">
          長さの合計: <strong>{{ totalFieldLength() }}</strong>
          <span class="length-total-note">（各 length の合算）</span>
        </span>
        <span class="length-total-row">
          count の合計: <strong>{{ totalFieldCountSum() }}</strong>
          <span class="length-total-note">（1 以上の count のみ加算）</span>
        </span>
      </p>

      <div formArrayName="entityFields" class="fields-list">
        @for (field of entityFields.controls; let i = $index; track field.get('_id')?.value) {
          <div [formGroupName]="i" class="field-item">
            <div class="reorder-actions">
              <button type="button" class="order-btn" (click)="moveFieldUp(i)" [disabled]="i === 0">▲</button>
              <button type="button" class="order-btn" (click)="moveFieldDown(i)" [disabled]="i === entityFields.length - 1">
                ▼
              </button>
            </div>
            <div class="field-inputs fixed-field-inputs">
              <div class="form-group mini">
                <label>項目名</label>
                <input type="text" formControlName="fieldName" placeholder="productId" />
              </div>
              <div class="form-group mini">
                <label>長さ</label>
                <input type="number" formControlName="length" placeholder="40" />
              </div>
              <div class="form-group mini">
                <label>トリム</label>
                <input type="text" formControlName="trimType" placeholder="rtrim" />
              </div>
              <div class="form-group mini">
                <label>型</label>
                <input type="text" formControlName="fieldType" placeholder="string" />
              </div>
              <div class="form-group mini">
                <label>件数<span class="field-hint">繰り返し</span></label>
                <input type="number" formControlName="count" min="1" placeholder="10" />
              </div>
              <div class="form-group mini">
                <label>書式<span class="field-hint">format</span></label>
                <input type="text" formControlName="format" placeholder="0" />
              </div>
              <div class="form-group mini">
                <label>配置<span class="field-hint">textAlignment</span></label>
                <input type="text" formControlName="textAlignment" placeholder="right" />
              </div>
            </div>
            <button type="button" class="remove-btn" (click)="removeField(i)">×</button>
          </div>
        }
      </div>
      <div class="mapping-actions">
        <button type="button" class="add-btn" (click)="addField()">+ フィールドを追加</button>
        <button
          type="button"
          class="reflect-keys-btn"
          (click)="reflectKeysFromJson()"
          [disabled]="reflectingKeys()"
        >
          @if (reflectingKeys()) {
            <span class="btn-loader"></span>反映中...
          } @else {
            <span class="icon">🔍</span>JSONのキーを反映
          }
        </button>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">🔄</span>
        <h2>ビュースキーマ / オプション</h2>
      </div>
      <app-view-schema-editor formControlName="viewSchemaJson" mode="fixed"></app-view-schema-editor>
      <div class="form-group">
        <label for="crossJoinOptionsJson">Cross Join Options (JSON)<span class="field-hint">クロス結合オプション（任意）</span></label>
        <textarea id="crossJoinOptionsJson" formControlName="crossJoinOptionsJson" rows="5" placeholder='{ "expandFields": [...] }'></textarea>
      </div>
      <div class="form-group">
        <label for="saveModelJson">Save Model Options (JSON)<span class="field-hint">保存モデルオプション（任意）</span></label>
        <textarea id="saveModelJson" formControlName="saveModelJson" rows="4" placeholder='{ "id": "..." }'></textarea>
      </div>
    </section>

    <footer class="form-footer">
      <button type="button" class="execute-btn" (click)="executeTask()" [disabled]="isExecuting()">
        @if (isExecuting()) {
          <span class="btn-loader"></span>実行中...
        } @else {
          ▶ 変換実行
        }
      </button>
      <button type="button" class="submit-btn" (click)="saveConfig()" [disabled]="isSaving()">
        @if (isSaving()) {
          <span class="btn-loader"></span>保存中...
        } @else {
          JSON→固定長設定を保存
        }
      </button>
    </footer>
  </form>

  @if (hasExecuted()) {
    <div class="results-section glass-form" style="margin-top: 2rem;">
      <div class="section-title">
        <span class="icon">✅</span>
        <h2>変換結果 ({{ configForm.get('savePath')?.value }})</h2>
      </div>
      @if (isLoadingResult()) {
        <div class="results-loading">結果を読み込んでいます...</div>
      } @else {
        <pre class="results-pre">{{ resultText() }}</pre>
      }
    </div>
  }
</div>
